/**
 * OnboardingReadingSite: the plan step while the crawl is still running
 * (Paper "Onboarding · 03a Your page · reading site").
 *
 * Left, the page we are reading in a browser frame: the captured screenshot
 * when the snapshot poll has one, otherwise a skeleton of a page. Right, what
 * the crawl is doing, wired to the generation job. The job reports only
 * queued / running / completed, so there are no per-step timings and no
 * "found N tools" row: we show nothing we did not measure.
 *
 * Presentational. The page owns the job, the snapshot poll and the 20s
 * `RESEARCH_CAP_MS` ceiling, after which the plan renders with heuristic drafts.
 * The CTA stays enabled throughout (Paper shows it disabled; CLAUDE.md wins:
 * the crawl wait must never gate activation).
 */

import { HugeiconsIcon } from "@hugeicons/react"
import { Tick01Icon } from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { ExperimentGenerationStatus } from "@/lib/api/onboardingTests"

export type ReadingStepState = "done" | "current" | "next"

export type ReadingStep = {
  id: "read" | "draft" | "mark"
  title: string
  detail: string
  state: ReadingStepState
}

/**
 * The three rows from what the job and the snapshot actually report.
 * "Read" is done once a screenshot of the page exists or the job has finished;
 * drafting is current after that; marking the changes lands with the job.
 */
export function readingSteps({
  domain,
  goalNoun,
  jobStatus,
  hasSnapshot,
}: {
  domain: string
  goalNoun: string
  jobStatus: ExperimentGenerationStatus | null
  hasSnapshot: boolean
}): ReadingStep[] {
  const completed = jobStatus === "completed"
  const read = hasSnapshot || completed
  return [
    {
      id: "read",
      title: "Read your homepage",
      detail: `${domain}, above and below the fold`,
      state: read ? "done" : "current",
    },
    {
      id: "draft",
      title: `Drafting tests for more ${goalNoun}`,
      detail: "Matching what we read to your goal",
      state: completed ? "done" : read ? "current" : "next",
    },
    {
      id: "mark",
      title: "Mark each change on your page",
      detail: "Numbered on the capture, like a teardown",
      state: completed ? "done" : "next",
    },
  ]
}

function StepMark({ state }: { state: ReadingStepState }) {
  if (state === "done") {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
        <HugeiconsIcon icon={Tick01Icon} size={11} strokeWidth={2.4} />
      </span>
    )
  }
  if (state === "current") {
    return (
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full shadow-[inset_0_0_0_1.5px_var(--foreground)]">
        <span className="size-2 rounded-full bg-foreground motion-safe:animate-pulse" />
      </span>
    )
  }
  return <span className="size-5 shrink-0 rounded-full shadow-[inset_0_0_0_1.5px_var(--ring)]" />
}

/** A page-shaped skeleton: nav, headline, two buttons, a hero image, a footer row. */
function PageSkeleton() {
  return (
    <div className="flex h-full flex-col gap-3.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Skeleton className="size-3.5" />
          <Skeleton className="h-3 w-10" />
        </div>
        <div className="hidden items-center gap-3.5 sm:flex">
          <Skeleton className="h-1.5 w-[34px]" />
          <Skeleton className="h-1.5 w-[34px]" />
          <Skeleton className="h-1.5 w-[34px]" />
          <Skeleton className="h-[22px] w-[74px] rounded-[5px]" />
          <Skeleton className="h-[22px] w-[52px] rounded-[5px]" />
        </div>
      </div>
      <div className="flex w-full max-w-[330px] flex-col gap-2 pt-[22px]">
        <Skeleton className="h-[22px] w-full" />
        <Skeleton className="h-[22px] w-3/4" />
        <Skeleton className="mt-1 h-1.5 w-[85%]" />
        <Skeleton className="h-1.5 w-2/3" />
        <div className="flex gap-2 pt-2">
          <Skeleton className="h-7 w-[77px] rounded-md" />
          <Skeleton className="h-7 w-[81px] rounded-md" />
        </div>
      </div>
      <div className="flex flex-col gap-3 pt-2.5">
        <Skeleton className="h-[120px] rounded-lg" />
        <div className="flex items-center gap-[18px]">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-2 w-10" />
          ))}
        </div>
      </div>
    </div>
  )
}

type OnboardingReadingSiteProps = {
  domain: string
  steps: ReadingStep[]
  /** The captured page, when the snapshot poll has one. */
  snapshotUrl?: string | null
  /** Label of the plan's primary action. Never disabled by the wait. */
  continueLabel: string
  onContinue: () => void
  className?: string
}

export function OnboardingReadingSite({
  domain,
  steps,
  snapshotUrl,
  continueLabel,
  onContinue,
  className,
}: OnboardingReadingSiteProps) {
  return (
    <section aria-labelledby="onboarding-reading-heading" className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-col gap-2">
        <h1
          id="onboarding-reading-heading"
          className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground sm:text-[28px] sm:leading-[34px]"
        >
          Reading {domain}
        </h1>
        <p className="text-sm leading-5 text-muted-foreground sm:text-base sm:leading-6">
          Usually under a minute. We started when you entered your site, so it is often done by now.
        </p>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[560px_minmax(0,1fr)]">
        {/* The page under the crawl, in a browser frame. */}
        <figure className="flex min-w-0 flex-col gap-2 rounded-xl bg-canvas p-2" aria-label={`${domain} as we read it`}>
          <div className="flex items-center gap-2 px-1.5 py-1">
            <div className="flex gap-[5px]" aria-hidden>
              <span className="size-2 rounded-full bg-foreground/20" />
              <span className="size-2 rounded-full bg-foreground/20" />
              <span className="size-2 rounded-full bg-foreground/20" />
            </div>
            <div className="flex h-[22px] min-w-0 flex-1 items-center rounded-md bg-card px-2.5">
              <span className="truncate font-mono text-[11px] leading-[14px] text-foreground/55">{domain}</span>
            </div>
          </div>
          <div className="h-[380px] overflow-hidden rounded-lg bg-card shadow-[0_0_0_1px_rgb(9_9_11/0.05)]">
            {snapshotUrl ? (
              <img
                src={snapshotUrl}
                alt={`Screenshot of ${domain}`}
                className="block h-full w-full object-cover object-top"
              />
            ) : (
              <div className="h-full px-5 py-5 sm:px-7">
                <PageSkeleton />
              </div>
            )}
          </div>
        </figure>

        {/* What the crawl is doing. Polite: a phase change is announced, focus stays put. */}
        <ol role="status" aria-live="polite" aria-label="Reading progress" className="flex min-w-0 flex-col">
          {steps.map((step) => (
            <li key={step.id} className="flex gap-3 border-b border-ui-hairline py-3.5">
              <StepMark state={step.state} />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p
                  className={cn(
                    "text-sm font-medium leading-5",
                    step.state === "next" ? "text-foreground/45" : "text-foreground",
                  )}
                >
                  {step.title}
                  <span className="sr-only">
                    {step.state === "done" ? ", done" : step.state === "current" ? ", in progress" : ", up next"}
                  </span>
                </p>
                <p className="text-[13px] leading-[18px] text-muted-foreground">{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
        <Button
          type="button"
          onClick={onContinue}
          className="h-11 shrink-0 rounded-lg bg-[var(--onboarding-action,oklch(0.54_0.21_42))] px-5 text-sm font-medium text-white hover:bg-[var(--onboarding-action,oklch(0.54_0.21_42))] hover:opacity-90"
        >
          {continueLabel}
        </Button>
        <p className="text-[13px] leading-[18px] text-muted-foreground">
          Your tests appear here as they are drafted.
        </p>
      </div>
    </section>
  )
}
